<script lang="ts" setup>
import { listTeeTimes } from '__generated__/listTeeTimes'
import {
  getCourse,
  getCourse_getCourse_GetCourseOutput_course,
  getCourse_getCourse_GetCourseOutput_course_attributes
} from '__generated__/getCourse'
import {
  getHolesForCourse,
  getHolesForCourse_listHoles_ListHolesOutput_holes
} from '__generated__/getHolesForCourse'
import { TeeTimesHoleFilterValue } from '__generated__/globalTypes'

import { onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'

import USInputDate from '@base/components/USInputDate/USInputDate.vue'
import USInputNumber from '@base/components/USInputNumber/USInputNumber.vue'
import USInputSelect from '@base/components/USInputSelect/USInputSelect.vue'
import USAccordion from '@base/components/USAccordion/USAccordion.vue'
import ClientOnly from '@base/components/ClientOnly/ClientOnly.vue'
import USShareButton from '@base/components/USShareButton/USShareButton.vue'
import USButton from '@base/components/USButton/USButton.vue'
import ShareButton from '@base/components/USShareButton/USShareButton.vue'
import MediaSlide from '@base/components/USMediaSlide/USMediaSlide.vue'
import MediaSlider from '@base/components/USMediaSlider/USMediaSlider.vue'
import TextTags from '@base/components/USTextTags/USTextTags.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USRatingStars from '@base/components/USRatingStars/USRatingStars.vue'
import USIcon from '@base/components/USIcon/USIcon.vue'
import ClubDetails from '@base/components/ClubDetails/ClubDetails.vue'
import CardRow from '@base/components/CardRow/CardRow.vue'

import { useClient } from '@base/api/useClient'
import { formatCoursePriceRange } from '@base/helpers/formatCoursePriceRange'
import { createDistanceContent } from '@base/lib/createDistanceContent'
import { createSlug } from '@base/lib/createSlug'
import getSlug from '@base/lib/getSlug'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'
import { Card, CardType } from '@base/_types/Card'
import { Toast, ToastSemantic } from '@base/_types/Toast'
import { useToastsStore } from '@base/store/toastsStore'
import { useRouterStore } from '@base/store/routerStore'

import TeeTimeTile from '@player/_components/TeeTimeTile/TeeTimeTile.vue'
import ClubAvatar from '@player/_components/ClubAvatar/ClubAvatar.vue'
import CreateEventButton from '@player/_components/CreateEventButton.vue'

import coursePlaceholder from '@player/_assets/placeholder.jpg'

import { playerTitle } from '@player/_lib/playerTitle'

import { GET_COURSE_QUERY } from '@player/courses/api/queries/getCourse'
import { GET_HOLES_FOR_COURSE_QUERY } from '@player/courses/api/queries/getCourseHoles'
import { bookingsRouteNames } from '@player/bookings/router/routeNames'
import { clubsRouteNames } from '@player/clubs/router/routeNames'
import { coursesRouteNames } from '@player/courses/router/routeNames'
import { eventsRouteNames } from '@player/events/router/routeNames'

import imageHolePlaceholder from '@player/_assets/hole-card-placeholder.jpg'

import { sortTeeTimes } from '../lib/sortTeeTimes'
import { filterTeeTimes } from '../lib/filterTeeTimes'
import { TeeTime } from '../lib/TeeTime'
import { props as propDefinition } from './props'

import { TEETIME_QUERY } from '@player/courses/api/queries/getTeeTimes'

// mock data
const mockData = ref()
mockData.value = {
  teeTimes: [
    {
      availability: 4,
      cost: 50,
      time: new Date(2022, 0, 31, 13, 0),
      id: 'tt-123'
    },
    {
      availability: 1,
      cost: 50,
      time: new Date(2022, 0, 31, 14, 0),
      id: 'tt-125'
    }
  ]
}

// Props
const props = defineProps(propDefinition)

// Static Variables
const client = useClient()
const router = useRouter()
const route = useRoute()
const toastsStore = useToastsStore()

const holesOptions = [
  { label: '9 Holes', value: TeeTimesHoleFilterValue.ALL9 },
  { label: '18 Holes', value: TeeTimesHoleFilterValue.ALL18 }
]

// Inputs

// Outputs
const routerStore = useRouterStore()
const loading = ref<boolean>(false)
const serverError = ref<boolean>(false)
const players = ref<number>(0)
const accordions = ref<{ heading: string }[]>([])
const teeTimes = ref<TeeTime[]>([])
const date = ref<Date>(new Date())
const groupedTeeTimes = ref<TeeTime[][]>([])
const fullClubAddress = ref('')
const currentURL = ref('')
const pageTitle = ref(playerTitle())
const holes = ref([])
date.value.setHours(0, 0, 0)

useHead({
  title: pageTitle
})

let end = new Date(date.value.getTime() + 86400000)

const courseData = ref<getCourse_getCourse_GetCourseOutput_course | null>(null)

const holesData = ref<
  getHolesForCourse_listHoles_ListHolesOutput_holes[] | null
>(null)

// Helper Functions
const reset = () => {
  loading.value = false
  serverError.value = false
  teeTimes.value = []
  groupedTeeTimes.value = []
  accordions.value = []
  date.value.setHours(0, 0, 0)
}

const handleToastSuccess = () => {
  toastsStore.pushToast({
    content: 'The URL has been copied to clipboard!',
    icon: 'check-circle',
    semantic: ToastSemantic.Success
  })
}

const clubAddress = (
  addressLine1: string,
  addressLine2: string,
  city: string,
  province: string,
  postalCode: string
) => {
  let fullAddress = ''

  if (addressLine1) {
    fullAddress += addressLine1 + ', '
  }

  if (addressLine2) {
    fullAddress += addressLine2 + ', '
  }

  if (city) {
    fullAddress += city + ', '
  }

  if (province) {
    fullAddress += province + ', '
  }

  if (postalCode) {
    fullAddress += postalCode
  }
  fullClubAddress.value = fullAddress
}

const generateClubLogo = (logo: string) => {
  return {
    src: ugcImageSrc(logo)[0]
  }
}

const findAttributesLabels = (
  attributes: [getCourse_getCourse_GetCourseOutput_course_attributes]
) => {
  let attributesLabels = <string[]>[]
  attributes.forEach((attr) => {
    attributesLabels.push(attr.name)
  })
  return attributesLabels
}

const convertHolesToCardRowProps = () => {
  let converted = <Card[]>[]

  holesData.value?.forEach((hole, idx) => {
    converted.push({
      type: CardType.CardHole,
      data: {
        title: `Hole ${idx + 1}`,
        par: hole.par || 0,
        foreground: {
          src: hole.gallery
            ? ugcImageSrc(hole.gallery[0] || '')[0]
            : imageHolePlaceholder,
          objectFit: 'cover'
        },
        background: {
          src: ugcImageSrc(hole.map || '')[0],
          objectFit: 'contain'
        },
        showMenu: false,
        to: {
          name: coursesRouteNames.HoleSingle,
          params: {
            holeIndex: idx + 1
          }
        }
      }
    })
  })

  return converted
}

onMounted(() => {
  currentURL.value = window.location.href
})

// Query Functions
const fetchData = async () => {
  reset()
  const slug = getSlug(props.slug)

  // Prepare Inputs + any Formating

  // Validate Inputs (optional, mainly for validating form inputs)

  // Fetch Data
  try {
    loading.value = true
    const { data: courseQuery } = await client.query<getCourse>({
      query: GET_COURSE_QUERY,
      variables: {
        slug: slug
      },
      fetchPolicy: 'cache-first'
    })

    if (!props.slug) {
      routerStore.is404 = true
      return
    } else if (
      !courseQuery ||
      courseQuery.getCourse.__typename !== 'GetCourseOutput' ||
      !courseQuery.getCourse.course
    ) {
      routerStore.is404 = true
      return
    }

    if (
      courseQuery.getCourse.course.club.__typename !== 'GetClubOutput' ||
      !courseQuery.getCourse.course.club ||
      !courseQuery.getCourse.course.club.club
    ) {
      routerStore.is404 = true
      return
    }

    clubAddress(
      courseQuery.getCourse.course.club.club.addressLine1 || '',
      courseQuery.getCourse.course.club.club.addressLine2 || '',
      courseQuery.getCourse.course.club.club.city || '',
      courseQuery.getCourse.course.club.club.province || '',
      courseQuery.getCourse.course.club.club.postalCode || ''
    )

    const { data: holesQuery } = await client.query<getHolesForCourse>({
      query: GET_HOLES_FOR_COURSE_QUERY,
      variables: {
        slug: slug,
        limit: 0
      },
      fetchPolicy: 'cache-first'
    })

    if (!holesQuery || holesQuery.listHoles.__typename !== 'ListHolesOutput') {
      routerStore.is404 = true
      return
    }

    courseData.value = courseQuery.getCourse.course
    holesData.value = holesQuery.listHoles.holes

    const expectedSlug = createSlug(
      courseQuery.getCourse.course.courseName,
      courseQuery.getCourse.course.slug
    )

    if (props.slug !== expectedSlug) {
      routerStore.redirect.code = 302
      await router.replace({
        params: {
          ...route.params,
          slug: expectedSlug
        }
      })
    }

    pageTitle.value = playerTitle(courseQuery.getCourse.course.courseName)
    // const { data } = await client.query<listTeeTimes>({
    //   query: TEETIME_QUERY,
    //   variables: {
    //     courseSlug: props.courseData.slug,
    //     startDate: date.value,
    //     endDate: end,
    //     min: Number(players.value.toString()),
    //     holes: holes.value[0]
    //   }
    // })

    // const { listTeeTimes } = data

    // if (
    //   listTeeTimes.__typename === 'ServerError' ||
    //   listTeeTimes.__typename === 'UnauthorizedError' ||
    //   listTeeTimes.__typename === 'UnauthenticatedError' ||
    //   listTeeTimes.__typename === 'ListTeeTimesErrorOutput'
    // ) {
    //   return
    // }

    // teeTimes.value = listTeeTimes.teeTimes.map((teeTime) => ({
    //   time: new Date(teeTime.time),
    //   cost: teeTime.cost,
    //   cost9: teeTime.cost9,
    //   availability: teeTime.availability
    // }))

    // teeTimes.value = mockData.value.teeTimes.map((teeTime) => ({
    //   // Type is missing because its supposed to have the type of listTeeTimes
    //   time: new Date(teeTime.time),
    //   cost: teeTime.cost,
    //   cost9: teeTime.cost9,
    //   availability: teeTime.availability,
    //   id: teeTime.id
    // }))

    for (let i = 0; i < 24; i++) {
      const filtered = filterTeeTimes(teeTimes.value, date.value, end, i, i)
      if (filtered.length) {
        groupedTeeTimes.value.push(sortTeeTimes(filtered))
        const time1 = new Date(
          groupedTeeTimes.value[
            groupedTeeTimes.value.length - 1
          ][0].time.getTime()
        ) //  Create a new Date to prevent mutating the first tee time time
        time1.setMinutes(0, 0, 0)
        const time2 = new Date(time1.getTime() + 3600000)
        accordions.value.push({
          heading: `${time1.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit'
          })} TO ${time2.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit'
          })}`
        })
      }
    }

    // const { data } = await client.query<...>({
    //  query: ...,
    //  variables: {
    //    page: page.value
    //  }
    // })

    // if (data...) {
    //   // Handle Success
    //  courses.value = data...
    // } else {
    //   // Handle Errors
    //   serverError.value = true
    // }
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    loading.value = false
  }
}

// Fetch Data when the URL changes the props
watch(props, async () => {
  await fetchData()
})

// Fetch data when the view is first rendered
await fetchData()
</script>

<template>
  <div class="course-single">
    <div class="grid__row">
      <div class="grid__column grid__column--12 margin-bottom--6">
        <div class="course-single__heading-wrapper body--p1--medium">
          <div class="course-single__heading">
            <p class="course-single__heading-text">
              {{ courseData.club.club.clubName }}
            </p>
            <p class="course-single__heading-text">
              {{ courseData.club.club.city }}
            </p>
            <p class="course-single__heading-text">
              {{ createDistanceContent(courseData.club.club.addressGeom) }}
            </p>
          </div>
          <div class="course-single__heading-button-container">
            <div>
                <USShareButton
                label="Share course"
                :share-url="currentURL"
                @succuss="handleToastSuccess"
              />
            </div>
            <div>
              <CreateEventButton
                :club-id="courseData.club.club.id"
                :club-name="courseData.club.club.clubName"
                :is-non-golf-club="courseData.club.club.isNonGolf"
                :event-route-name="eventsRouteNames.EventDetail"
                event-route-param="eventId"
              />
            </div>
          </div>
        </div>
        <h1 class="heading--h2 margin-top--8">
          {{ courseData.courseName }}
        </h1>
      </div>
    </div>
    <MediaSlider
      v-if="courseData.gallery.length"
      class="course-single__media-slider"
    >
      <MediaSlide
        v-for="(img, idx) in courseData.gallery.map((img) => ugcImageSrc(img))"
        :key="idx"
        :img-args="{ src: img[0], srcset: img[1] }"
        :lazy="true"
      />
    </MediaSlider>
    <MediaSlider v-else class="course-single__media-slider">
      <MediaSlide :img-args="{ src: coursePlaceholder }" :lazy="true" />
    </MediaSlider>
    <div class="grid__row">
      <div class="grid__column grid__column--12 margin-bottom--5">
        <div v-if="courseData.attributes" class="course-single__tags">
          <TextTags :tags="findAttributesLabels(courseData.attributes)" />
        </div>
      </div>
      <div class="grid__column grid__column--12 grid__column--breakpoint--7--6">
        <p class="heading--h3">Overview</p>
        <p class="body--p1--regular margin-bottom--5 margin-top--6">
          {{ courseData.description }}
        </p>
        <div class="course-single__course-info">
          <div
            v-if="courseData.numHoles"
            class="course-single__course-info-item"
          >
            <p class="course-single__course-info-heading body--p1--medium">
              Holes
            </p>
            <p class="course-single__course-info-info heading--h3">
              {{ courseData.numHoles }}
            </p>
          </div>
          <div v-if="courseData.par" class="course-single__course-info-item">
            <p class="course-single__course-info-heading body--p1--medium">
              Par
            </p>
            <p class="course-single__course-info-info heading--h3">
              {{ courseData.par }}
            </p>
          </div>
          <div
            v-if="courseData.pricing"
            class="course-single__course-info-item"
          >
            <p class="course-single__course-info-heading body--p1--medium">
              Pricing
            </p>
            <p class="course-single__course-info-info heading--h3">
              {{ formatCoursePriceRange(courseData.pricing) }}
            </p>
          </div>
        </div>
      </div>
      <div class="grid__column grid__column--12 grid__column--breakpoint--7--6">
        <ImageWrapper :src="ugcImageSrc(courseData.map)[0]" />
      </div>
      <div
        v-if="holesData && holesData.length"
        class="course-single__holes grid__column grid__column--12"
      >
        <p class="heading--h3 margin-bottom--6 margin-top--4">Holes</p>
      </div>
    </div>
    <CardRow :cards="convertHolesToCardRowProps(holesData)" />
    <div class="grid__row">
      <!-- <div
        class="margin-top--4 grid__column grid__column--12 grid__column--breakpoint--7--4 grid__column--breakpoint--13--3"
      >
        <p class="heading--h3 margin-bottom--6">Tee Times</p>
        <ClientOnly>
          <USInputDate
            v-model="date"
            :min-date="new Date()"
            heading="Date"
            place-holder="DD-MM-YYYY"
            class="course-single__input-calender"
            @update:modelValue="fetchData"
          />
        </ClientOnly>
        <USInputNumber
          v-model="players"
          heading="Players"
          :min="1"
          :max="4"
          class="course-single__input"
          @update:modelValue="fetchData"
        />
        <USInputSelect
          v-model="holes"
          heading="Number of Holes"
          :heading-level="3"
          :options="holesOptions"
          class="course-single__input"
          @update:modelValue="fetchData"
        />
      </div> -->
      <!-- <div
        class="margin-top--4 grid__column--12 grid__column--breakpoint--7--8 grid__column--breakpoint--13--offset--1"
      >
        <USAccordion v-if="accordions.length" :accordions="accordions">
          <template
            v-for="(teeTimes, idx) in groupedTeeTimes"
            :key="idx"
            #[`accordion-${idx}`]
            class="course-single__tiles"
            ]
          >
            <router-link
              v-for="teeTime in teeTimes"
              :key="teeTime.time"
              :to="{
                name: bookingsRouteNames.BookingNew,
                query: { type: 'teeTime', id: teeTime.id }
              }"
            >
              <ClientOnly>
                <TeeTimeTile
                  :time="
                    teeTime.time.toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit'
                    })
                  "
                  :spots="teeTime.availability"
                  :price="teeTime.cost"
                  class="course-single__tiles-tile"
                />
                Price used to be set to this [ holes[0] === 'ALL9' ? teeTime.cost9 : teeTime.cost ] currently not used for mock data
              </ClientOnly>
            </router-link>
          </template>
        </USAccordion>
        <h1 v-else class="heading--h3 course-single__no-results">
          Hmmm…we don’t seem to have any tee times available that meet your
          search criteria. Try searching by a different date, max number of
          players
        </h1>
      </div> -->

      <!-- TODO Update club details -->
      <ClubDetails
        class="margin-top--3"
        heading="Visit the Club"
        :amenities="courseData.club.club.amenities"
        :club-avatar="generateClubLogo(courseData.club.club.logo)"
        :club-name="courseData.club.club.clubName"
        :club-address="fullClubAddress"
        :club-rating="courseData.club.club.averageRating || 0"
        :club-phone-number="courseData.club.club.phoneNumber"
        :to="{
          name: clubsRouteNames.ClubSingle,
          params: {
            slug: createSlug(
              courseData.club.club.clubName,
              courseData.club.club.slug
            )
          }
        }"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

.course-single {
  color: var(--color--neutral-a1);

  @media (min-width: breakpoint.$breakpoint--tablet) {
    margin-bottom: 250px;
  }

  &__heading-wrapper {
    display: flex;
    flex-direction: column-reverse;

    @media (min-width: breakpoint.$breakpoint--tablet) {
      flex-direction: row;
      justify-content: space-between;
    }
  }

  &__heading {
    color: var(--color--neutral-a3);
    display: flex;
    flex-direction: column-reverse;
    margin-top: var(--spacing--7);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      flex-direction: row;
      margin-top: 0;
    }
  }

  &__heading-text {
    margin-right: var(--spacing--7);
    text-transform: uppercase;

    &:not(:last-child) {
      margin-bottom: var(--spacing--8);

      @media (min-width: breakpoint.$breakpoint--tablet) {
        margin-bottom: 0;
      }
    }
  }

  &__heading-button-container {
    display: flex;

    > div:first-child {
      margin-right: var(--spacing--6);
    }
  }

  &__media-slider {
    margin-bottom: var(--spacing--6);
  }

  &__course-info {
    display: flex;
    flex-flow: row;
  }

  &__course-info-item {
    margin-right: var(--spacing--6);
  }

  &__course-info-heading {
    color: var(--color--primary-b2);
    text-transform: uppercase;
  }

  &__input {
    &:not(:last-child) {
      margin-bottom: var(--spacing--5);
    }
  }

  &__input-calender {
    order: 1;
    padding-bottom: var(--spacing--5);
    margin: 0 auto;
    margin-top: var(--spacing--5);
    width: 100%;

    @media (min-width: breakpoint.$breakpoint--tablet) {
      order: 0;
      margin-top: 0;
    }
  }

  &__tiles-tile {
    cursor: pointer;
  }

  &__no-results {
    text-align: center;
  }

  &__club {
    align-items: center;
    display: flex;
    flex-flow: row;
  }

  &__club-name {
    margin-left: var(--spacing--6);
  }

  &__club-details {
    align-items: center;
    display: flex;
    flex-direction: row;
  }

  &__club-detail {
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-basis: calc(50% - var(--spacing--5));
    margin-right: var(--spacing--5);
  }
}

:deep(.us-accordion__description) {
  display: flex;
  flex-flow: row wrap;

  .tee-time-tile {
    margin-bottom: var(--spacing--9);
    margin-right: var(--spacing--9);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      margin-bottom: var(--spacing--6);
      margin-right: var(--spacing--6);
    }
  }
}

:deep(.us-input--number) {
  width: 100%;

  .number-input-wrapper {
    max-width: 100%;
    width: 100%;
  }
}
</style>
